/* =========================================================================
   ClueVine — Bouquet (four words at once)
   Four hidden words, one shared set of guesses. Every guess is scored against
   all four boards at once; solve them all before your guesses run out. Reuses
   Cipher's clue evaluation — a composite that proves the engine composes.
   ========================================================================= */

import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';
import { evaluateGuess } from '../cipher/logic';

export interface BouquetConfig {
  count: number;
  length: number;
  maxGuesses: number;
}

export interface BouquetState {
  answers: string[];
  length: number;
  maxGuesses: number;
  guesses: string[];
  current: string;
  solvedAt: (number | null)[]; // index of the guess that solved each board
}

export type BouquetMove =
  | { type: 'add'; ch: string }
  | { type: 'remove' }
  | { type: 'submit' };

export const BOUQUET_DIFFICULTIES: DifficultyLevel<BouquetConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4 words · 10 guesses', scoreMultiplier: 0.9, config: { count: 4, length: 5, maxGuesses: 10 } },
  { id: 'standard', label: 'Garden', blurb: '4 words · 9 guesses', scoreMultiplier: 1, config: { count: 4, length: 5, maxGuesses: 9 } },
  { id: 'hard', label: 'Thicket', blurb: '4 words · 8 guesses', scoreMultiplier: 1.4, config: { count: 4, length: 5, maxGuesses: 8 } },
];

function computeSolvedAt(answers: string[], guesses: string[]): (number | null)[] {
  return answers.map((a) => {
    const idx = guesses.indexOf(a);
    return idx >= 0 ? idx : null;
  });
}

export function generate(ctx: GenerateContext<BouquetConfig>): BouquetState {
  const { count, length, maxGuesses } = ctx.difficulty.config;
  const pool = ctx.dictionary.answersByLength(length);
  const shuffled = ctx.prng.shuffle(pool);
  const fresh = shuffled.filter((w) => !ctx.avoid.has(w));
  const source = fresh.length >= count ? fresh : shuffled;
  const answers = source.slice(0, count);

  return { answers, length, maxGuesses, guesses: [], current: '', solvedAt: computeSolvedAt(answers, []) };
}

export function solvedCount(state: BouquetState): number {
  return state.solvedAt.filter((x) => x !== null).length;
}

export function status(state: BouquetState): PuzzleStatus {
  const allSolved = state.solvedAt.every((x) => x !== null);
  if (allSolved) return 'won';
  if (state.guesses.length >= state.maxGuesses) return 'lost';
  return 'playing';
}

export function validateMove(state: BouquetState, move: BouquetMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  if (state.current.length < state.length) return { ok: false, reason: 'Not enough letters' };
  if (state.guesses.includes(state.current)) return { ok: false, reason: 'Already guessed' };
  return { ok: true };
}

export function reduce(state: BouquetState, move: BouquetMove): BouquetState {
  switch (move.type) {
    case 'add':
      if (state.current.length >= state.length) return state;
      return { ...state, current: state.current + move.ch };
    case 'remove':
      return state.current ? { ...state, current: state.current.slice(0, -1) } : state;
    case 'submit': {
      if (state.current.length < state.length || state.guesses.includes(state.current)) return state;
      const guesses = [...state.guesses, state.current];
      return { ...state, guesses, current: '', solvedAt: computeSolvedAt(state.answers, guesses) };
    }
    default:
      return state;
  }
}

export function handleKey(state: BouquetState, key: InputKey): BouquetMove | null {
  if (key.type === 'char') {
    return state.current.length < state.length ? { type: 'add', ch: key.value } : null;
  }
  if (key.type === 'backspace') {
    return state.current ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function hint(state: BouquetState): HintOutcome<BouquetState> | null {
  if (status(state) !== 'playing') return null;
  const boardIdx = state.solvedAt.findIndex((x) => x === null);
  if (boardIdx < 0) return null;
  const answer = state.answers[boardIdx];

  // Find a position not yet revealed as correct on that board.
  const known = new Set<number>();
  for (const g of state.guesses) {
    evaluateGuess(g, answer).forEach((clue, i) => {
      if (clue === 'correct') known.add(i);
    });
  }
  let pos = 0;
  while (pos < answer.length && known.has(pos)) pos += 1;
  if (pos >= answer.length) return null;
  return { message: `One word has “${answer[pos].toUpperCase()}” at position ${pos + 1}.`, penalty: 60 };
}

export function score(state: BouquetState): ModeScore {
  if (status(state) !== 'won') {
    const solved = solvedCount(state);
    return {
      base: solved * 120,
      lines: [{ label: `${solved}/${state.answers.length} solved`, value: solved * 120, kind: 'base' }],
      parMs: state.length * state.answers.length * 6000,
    };
  }
  const base = 300 + (state.maxGuesses - state.guesses.length) * 130 + state.answers.length * 80;
  return {
    base,
    lines: [{ label: `All ${state.answers.length} in ${state.guesses.length} guesses`, value: base, kind: 'base' }],
    parMs: state.length * state.answers.length * 6000,
    perfect: state.guesses.length === state.answers.length,
  };
}

export function summarize(state: BouquetState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won
      ? `All ${state.answers.length} in ${state.guesses.length}!`
      : `Solved ${solvedCount(state)}/${state.answers.length}`,
    detail: state.answers.map((a) => a.toUpperCase()).join(' · '),
    revealLabel: 'Words',
    avoidKeys: state.answers,
    shareGrid: [won ? `💐 all ${state.answers.length} in ${state.guesses.length}` : `🥀 ${solvedCount(state)}/${state.answers.length}`],
  };
}

export function deserialize(raw: unknown): BouquetState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<BouquetState>;
  if (!Array.isArray(s.answers) || !Array.isArray(s.guesses) || typeof s.current !== 'string') return null;
  if (typeof s.length !== 'number' || typeof s.maxGuesses !== 'number') return null;
  const answers = s.answers.filter((a): a is string => typeof a === 'string');
  const guesses = s.guesses.filter((g): g is string => typeof g === 'string');
  return {
    answers,
    length: s.length,
    maxGuesses: s.maxGuesses,
    guesses,
    current: s.current,
    solvedAt: computeSolvedAt(answers, guesses),
  };
}
